/**
 * page-org — one organization, routed at `/orgs/:orgId`, as four tabs: members,
 * invitations, settings and quota.
 *
 * Implements:
 * - `SC-F100` / `SC-F101` — the organization as the tenancy root, with the
 *   personal organization marked and its owner rule stated (`SC-S101`).
 * - `SC-F102` — the role ladder viewer < member < maintainer < owner, applied
 *   through [atLeast] and [canManage] rather than by comparing strings.
 * - `SC-F103` — role changes and removal, with **the last-owner invariant made
 *   visible**: the control that would strand the organization is disabled and
 *   says why, rather than being offered and then refused (`SC-S107`,
 *   `SC-S108`).
 * - `SC-F104` / `SC-F105` — invitations: create, list with expiry, resend and
 *   revoke, and the per-actor send budget surfaced honestly when it is hit
 *   (`SC-S104`, `SC-S105`, `SC-S106`).
 * - `SC-F106` — rename and settings, owner-only, with `If-Match` (`SC-S109`).
 * - `SC-F107` — ownership transfer, present and reachable, because organization
 *   lockout has no self-service recovery once the last owner is gone
 *   (`SC-S110`).
 * - `SC-F109` — the org-scoped navigation this screen sits in.
 * - `SC-F112` — the per-organization security settings, now **enforced** rather
 *   than displayed. Every switch on the panel is one the running build applies,
 *   each carries the boundary of what it applies (`limits` on the payload), and
 *   the one setting the platform cannot enforce per organization — session
 *   lifetime — is stated as absent with its reason instead of being offered
 *   (`concept.md` §7 principle 8). The previous surface rendered four switches
 *   disabled and said the control plane "neither stores nor enforces" them, which
 *   was already false for two of them; that is the sentence this screen removes.
 * - `SC-F914` — duty classes. A member's subtracted classes are on their roster
 *   row, and an owner edits them from the Settings tab. The **coverage refusal is
 *   shown, never bypassed**: a configuration that would leave the organization
 *   with nobody able to publish or administer is refused server-side with the
 *   class named, and this screen renders that reason rather than a generic error.
 * - `SC-F020` — the per-organization enforced-SSO switch, on the same panel as
 *   require-passkey because both can strand a member and both need their
 *   break-glass explanation next to them.
 * - `SC-F901` — entitlement consumption, shown as consumption and not as an
 *   alarm.
 *
 * Every role gate here mirrors the permission matrix in `01` §4.4.1: members are
 * readable by a viewer, invitations need maintainer, settings need owner, and
 * plan/quota need maintainer (row 12). The server checks all of it again — these
 * gates exist so a viewer sees a disabled control naming the role it needs
 * rather than a 403 they have to interpret (`SC-S103`).
 */

import {
  LitElement,
  css,
  html,
  nothing,
  type PropertyValues,
  type TemplateResult,
} from "lit";
import { customElement, property, state } from "lit/decorators.js";

import {
  DUTIES,
  DUTY_LABELS,
  ROLES,
  ROLE_LABELS,
  atLeast,
  getDuties,
  getOrgWithETag,
  getPlan,
  getQuotas,
  getSecurity,
  setDuties,
  transferOwnership,
  updateOrg,
  updateSecurity,
  type Duty,
  type MemberDuties,
  type OrgSecurity,
  type Organization,
  type Plan,
  type Quota,
  type Role,
  type SecurityLimits,
} from "../api/orgs.js";
import {
  canManage,
  listMembers,
  removeMember,
  setMemberRole,
  type Member,
} from "../api/members.js";
import {
  createInvite,
  listInvites,
  resendInvite,
  revokeInvite,
  type Invite,
} from "../api/invites.js";
import {
  confirmStepUpOtp,
  currentUser,
  requestStepUpOtp,
  verifyWithPasskey,
  type StepUpTarget,
} from "../api/auth.js";
import { ApiError, parseError, type QuotaDetail } from "../api/errors.js";
import {
  getOrgInstanceDefaults,
  setOrgInstanceDefaults,
  type OrgInstanceDefaults,
} from "../api/instances.js";
import { hrefFor, navigate } from "../nav.js";
import {
  toast,
  type BnBadgeVariant,
  type BnColumn,
  type BnKvRow,
  type BnRow,
  type BnSelectChangeDetail,
  type BnSelectOption,
  type BnTab,
  type BnTabsChangeDetail,
} from "../components/index.js";

/* ── small shared helpers ────────────────────────────────────────────────── */

/** roleVariant keeps a role's tone identical on every screen that shows one. */
function roleVariant(role: Role | undefined): BnBadgeVariant {
  switch (role) {
    case "owner":
      return "info";
    case "maintainer":
      return "success";
    default:
      return "neutral";
  }
}

/** fmtDate renders an API timestamp as a plain local date, or an em dash. */
function fmtDate(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * expiryLabel renders how long a pending invitation has left. `SC-S105` makes
 * the internal view deliberately more precise than the anonymous one: a
 * maintainer already knows the organization exists, so "expired" is safe to say
 * here and unsafe on the public lookup.
 */
function expiryLabel(iso: string): { text: string; expired: boolean } {
  if (!iso) return { text: "—", expired: false };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { text: iso, expired: false };
  const hours = Math.floor((d.getTime() - Date.now()) / 3_600_000);
  if (hours <= 0) return { text: "expired", expired: true };
  if (hours < 24) return { text: `in ${hours}h`, expired: false };
  return { text: `in ${Math.floor(hours / 24)}d`, expired: false };
}

/** The `quota` ids `06` §1.4.1 and `GET .../quotas` share, mapped to copy. The
 * `quota_` prefix is stripped first because `05` §3.3 spells the storage columns
 * `quota_instances` while the wire may carry the bare dimension. */
const QUOTA_LABELS: Record<string, string> = {
  instances: "Instances",
  datasources_per_instance: "Datasources per instance",
  members: "Members",
  audit_retention_days: "Audit retention (days)",
};

function quotaLabel(id: string): string {
  const key = id.replace(/^quota_/, "");
  return (
    QUOTA_LABELS[key] ??
    key.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase())
  );
}

/**
 * The `SC-F112` switch set. Every entry here is a setting the running build
 * ENFORCES, and each `hint` says where the enforcement stops — the boundary comes
 * from the payload's `limits` block rather than from this table, so the screen
 * cannot drift from the server's own statement of what it does.
 *
 * `field` is the key on [OrgSecurity] and on the PATCH body, so a toggle is one
 * lookup rather than a switch statement.
 */
const SECURITY_SETTINGS: Array<{
  field: "require_passkey" | "enforced_sso" | "require_test_gate" | "suppress_provenance";
  label: string;
  hint: string;
  /** limit names which `limits` field states this switch's boundary, or "" when
   * the switch has no boundary worth stating. */
  limit: keyof SecurityLimits | "";
}> = [
  {
    field: "require_passkey",
    label: "Require a passkey for members who have one",
    hint: "A member who has enrolled a passkey can no longer sign in with an emailed code, which is what closes the phishing-relay path against a phishing-resistant account.",
    limit: "passkey_enforcement",
  },
  {
    field: "enforced_sso",
    label: "Require this organization's identity provider",
    hint: "A member who has signed in with the provider at least once can no longer use an emailed code. Configure the provider, the claimed domains and the break-glass window on the identity page.",
    limit: "sso_enforcement",
  },
  {
    field: "require_test_gate",
    label: "Require the policy test suite to pass before publishing",
    hint: "The pre-publish gate always blocks on validation. This adds the test suite to it, for every instance in this organization.",
    limit: "",
  },
  {
    field: "suppress_provenance",
    label: "Suppress provenance labels in rendered YAML",
    hint: "Omits the Cloud-authored labels and annotations from exported YAML, for customers who diff rendered output against a hand-written baseline.",
    limit: "",
  },
];

/**
 * LIMIT_COPY turns the payload's `limits` vocabulary into one sentence a reader
 * can act on. The keys are the server's values, so a value this bundle does not
 * know renders verbatim rather than silently as nothing — a build that learned a
 * new enforcement mode must not be described by an older screen as having none.
 */
const LIMIT_COPY: Record<string, string> = {
  enrolled_only:
    "Enforced for members who have enrolled a passkey. A member with no passkey keeps the emailed code, because every account must retain one method that needs no hardware — otherwise this switch would be a lockout rather than a control.",
  linked_only:
    "Enforced for members who have signed in with the provider at least once. A member who never has keeps the emailed code, for the same reason.",
  control_plane_api:
    "Applies to this API and this console only. It cannot restrict who reaches your gateway: queries go from the client to the edge to your instance and never through the control plane.",
  deployment_wide:
    "Session lifetime is a deployment-wide setting and cannot be set per organization on this build: one session spans every organization an account belongs to, and the sign-in token carries no issue time to compare against.",
};

/* ── the element ─────────────────────────────────────────────────────────── */

/** The four tabs, in display order. Ids double as `?tab=` values and as slot
 * names, so a tab is linkable. */
const TABS: BnTab[] = [
  { id: "members", label: "Members" },
  { id: "invitations", label: "Invitations" },
  { id: "settings", label: "Settings" },
  { id: "quota", label: "Quota and usage" },
];

/**
 * page-org renders one organization.
 *
 *   <page-org .orgId=${orgId}></page-org>
 */
@customElement("page-org")
export class PageOrg extends LitElement {
  /** The organization record id, from the route. */
  @property() orgId = "";

  /* ── the organization itself ──────────────────────────────────────── */
  @state() private org: Organization | null = null;
  @state() private loading = true;
  @state() private failure: ApiError | null = null;
  /** The `ETag` of the loaded organization, sent back as `If-Match` on save. */
  private etag = "";

  @state() private tab = "members";

  /* ── instance defaults (`SC-F110`) ─────────────────────────────────── */
  //
  // A separate load from the organization record because it is a separate route:
  // `internal/instances` owns it, so it carries the instance vocabulary and its
  // own role floor. Folding it into the organization read would put an instance
  // concern in `internal/accounts`, which cannot import `internal/orchestrator`
  // and must not restate a size enum it does not own.
  @state() private defaults: OrgInstanceDefaults | null = null;
  @state() private defaultsLoading = false;
  @state() private defaultsFailure: ApiError | null = null;
  @state() private defaultsSaving = false;
  /** The form's own copy, so an unsaved edit is not lost by a background reload
   * and so `""` (clear) is expressible. */
  @state() private formDefaultSize = "";

  /* ── members ──────────────────────────────────────────────────────── */
  @state() private members: Member[] = [];
  @state() private membersLoading = false;
  @state() private membersFailure: ApiError | null = null;
  /** Account id of the row with an in-flight write; disables that row only. */
  @state() private busyAccount = "";
  @state() private removeTarget: Member | null = null;
  @state() private removing = false;
  /** Typed confirmation for removing an **owner**. `01` §4.4.1 row 7 gates that
   * row at MVP on "owner-only plus typed confirmation"; this is the typing. */
  @state() private removeConfirmText = "";
  /**
   * The last-owner refusal, held on screen rather than toasted away.
   * `SC-S108` requires the refusal to carry the forward path, and a four-second
   * toast is not a place to put one.
   */
  @state() private ownerBlock = "";

  /* ── ownership transfer (`SC-F107`) ──────────────────────────────── */
  @state() private nominee = "";
  @state() private transferConfirm = false;
  @state() private transferring = false;

  /* ── invitations ─────────────────────────────────────────────────── */
  @state() private invites: Invite[] = [];
  @state() private invitesLoading = false;
  @state() private invitesFailure: ApiError | null = null;
  @state() private inviteEmail = "";
  @state() private inviteRole: Role = "member";
  @state() private inviting = false;
  @state() private inviteError = "";
  @state() private inviteFailure: ApiError | null = null;
  @state() private busyInviteId = "";

  /* ── settings ────────────────────────────────────────────────────── */
  // Named for the wire: `formName` is the RFC-1123 label under the "URL label"
  // field, `formDisplayName` is the prose under "Name" (`05` §3.3).
  @state() private formName = "";
  @state() private formDisplayName = "";
  @state() private saving = false;
  @state() private saveFailure: ApiError | null = null;
  @state() private saveError = "";

  /* ── security settings (`SC-F112`) and duty classes (`SC-F914`) ──── */
  //
  // A separate load from the organization record because it is a separate route
  // with a different role floor: reading is a viewer's, writing an owner's. The
  // four states `SC-S907` requires are all here — `securityLoading` is the
  // spinner, `securityFailure` the error with its code and request id,
  // `security === null` after a load the empty state, and the populated form
  // otherwise.
  @state() private security: OrgSecurity | null = null;
  @state() private securityLoading = false;
  @state() private securityFailure: ApiError | null = null;
  @state() private securitySaving = false;
  /** The refusal that must stay on screen rather than being toasted away: an
   * allowlist that excludes the caller cannot be saved, and the reason is the
   * whole message (`SC-S103`). */
  @state() private securityBlock = "";
  /** The allowlist as typed, one CIDR per line, so an unsaved edit survives a
   * re-render and so clearing the list is expressible. */
  @state() private formAllowlist = "";

  /** The member whose duty classes are being edited, or "" for none. */
  @state() private dutyTarget = "";
  @state() private dutyDraft: Duty[] = [];
  @state() private dutySaving = false;
  /** The coverage refusal, held on screen: `SC-F914`'s whole safety property is
   * that a configuration which would leave a class with no holder is refused
   * WITH the class named, and a four-second toast is not a place to put one. */
  @state() private dutyBlock = "";
  @state() private dutyFailure: ApiError | null = null;

  /* ── quota ───────────────────────────────────────────────────────── */
  @state() private quotas: Quota[] = [];
  @state() private plan: Plan | null = null;
  @state() private quotaLoading = false;
  @state() private quotaFailure: ApiError | null = null;

  /* ── step-up ceremony ────────────────────────────────────────────── */
  @state() private verifyPrompt: { heading: string; explain: string } | null =
    null;
  @state() private verifyStage: "choose" | "code" = "choose";
  @state() private verifyCode = "";
  @state() private verifyBusy = false;
  @state() private verifyError = "";
  private verifyOtpId = "";
  private verifyTarget: StepUpTarget = { action: "change_owner" };
  private verifyResolve: ((grant: string) => void) | null = null;

  /** The signed-in account id, used to mark "you" and to allow self-leave. */
  private readonly me = currentUser()?.id ?? "";

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .back {
      display: inline-block;
      margin-bottom: var(--bino-space-md);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      text-decoration: none;
      border-radius: var(--bino-radius-sm);
    }
    .back:hover {
      color: var(--bino-text);
    }
    .back:focus-visible {
      outline: none;
      box-shadow: var(--bino-focus-ring-shadow);
    }
    .head {
      margin-bottom: var(--bino-space-lg);
    }
    h1 {
      margin: 0;
      font-size: var(--bino-font-size-lg);
      font-weight: 600;
    }
    .meta {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: var(--bino-space-sm);
      margin-top: var(--bino-space-xs);
    }
    .slug {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    h2 {
      margin: 0 0 var(--bino-space-sm);
      font-size: var(--bino-font-size-md);
      font-weight: 600;
    }
    p.copy {
      margin: 0 0 var(--bino-space-md);
      max-width: 66ch;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
    p.copy:last-child {
      margin-bottom: 0;
    }
    p.copy strong {
      color: var(--bino-text);
    }
    code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
    }
    .error-text {
      color: var(--bino-error);
    }
    .gap {
      margin-top: var(--bino-space-lg);
    }
    .row {
      display: flex;
      align-items: flex-end;
      flex-wrap: wrap;
      gap: var(--bino-space-sm);
    }
    .row bn-field {
      margin-bottom: 0;
    }
    .grow {
      flex: 1 1 18rem;
      min-width: 12rem;
    }
    .role-field {
      flex: 0 0 13rem;
    }

    /* Roster and invitation cells */
    .who {
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
    }
    .who .name {
      display: inline-flex;
      align-items: center;
      gap: var(--bino-space-xs);
      font-weight: 600;
      color: var(--bino-text);
    }
    .mono {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .when {
      font-size: var(--bino-font-size-base);
      color: var(--bino-text-secondary);
      white-space: nowrap;
    }
    .cell-actions {
      display: flex;
      gap: var(--bino-space-xs);
      justify-content: flex-end;
    }
    /* The reason a control is disabled. It is text, not a tooltip: a disabled
       button is not focusable, so a title attribute alone would be unreachable
       by keyboard and unannounced by a screen reader. */
    .reason {
      display: block;
      max-width: 30ch;
      margin-top: 0.25rem;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      text-align: right;
    }
    .role-cell {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      min-width: 9rem;
    }
    .role-cell .reason {
      text-align: left;
      max-width: 26ch;
      margin-top: 0;
    }

    /* Failure block: distinct from an empty state, because "nothing here" and
       "we could not find out" are different answers and only one of them carries
       a reference a support engineer can look up. */
    .failure {
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-md);
      padding: var(--bino-space-sm) 0;
    }
    .failure h2,
    .failure p {
      margin: 0;
    }
    .failure p {
      max-width: 62ch;
      color: var(--bino-text-secondary);
      font-size: var(--bino-font-size-md);
    }
    .failure .actions {
      display: flex;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
    }

    /* Quota meters (SC-F901) — consumption, not an alarm: one tone at every
       level, and the numbers do the talking. */
    .quota-row + .quota-row {
      margin-top: var(--bino-space-lg);
    }
    .quota-head {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-xs);
    }
    .quota-name {
      font-size: var(--bino-font-size-md);
      font-weight: 500;
    }
    .quota-value {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .meter {
      height: 0.5rem;
      border-radius: var(--bino-radius-pill);
      background: var(--bino-surface-inset);
      overflow: hidden;
    }
    .meter-fill {
      height: 100%;
      border-radius: var(--bino-radius-pill);
      background: var(--bino-primary);
    }
    .features {
      display: flex;
      flex-wrap: wrap;
      gap: var(--bino-space-xs);
    }

    /* SC-F914 duty badges on a roster row: they wrap under the role rather than
       stretching the column, because a member can be excluded from all four. */
    .badges {
      display: inline-flex;
      flex-wrap: wrap;
      gap: var(--bino-space-xs);
      margin-left: var(--bino-space-xs);
    }

    /* SC-F112 limit statements: the boundary of what a switch enforces, rendered
       next to the switch rather than in documentation. Secondary tone because it
       is explanation and not status — a warning tone would read as "something is
       wrong" for a control that is working exactly as specified. */
    .limit {
      margin: 0 0 var(--bino-space-sm);
      max-width: 72ch;
      color: var(--bino-text-secondary);
      font-size: var(--bino-font-size-sm);
      line-height: 1.5;
    }

    /* SC-F112 setting rows */
    .setting {
      display: flex;
      align-items: flex-start;
      gap: var(--bino-space-sm);
      padding: var(--bino-space-md) 0;
      border-top: 1px solid var(--bino-border);
    }
    .setting bn-checkbox {
      flex: 1 1 auto;
    }
    .setting bn-badge {
      flex: none;
    }
  `;

  /* ── loading ─────────────────────────────────────────────────────── */

  override connectedCallback() {
    super.connectedCallback();
    // The tab rides in `?tab=`, so a link to one tab of one organization is a
    // link somebody can paste into a ticket. Loading is driven from `updated`
    // alone — the router sets `orgId` before the first update, and loading from
    // both places would fetch the roster twice on every navigation.
    //
    // `/orgs/:orgId/members` and `/orgs/:orgId/invitations` are routes in their
    // own right (`router.ts`) that render this element, so the trailing path
    // segment is the fallback source for the initial tab. `?tab=` wins when both
    // are present: it is the spelling [onTab] writes, so a reader who switched
    // tabs on `/orgs/:id/members` and then reloaded sees the tab they left on.
    const query = new URLSearchParams(location.search).get("tab");
    const segment = location.pathname.split("/").filter(Boolean).pop();
    const wanted = query || segment;
    if (wanted && TABS.some((t) => t.id === wanted)) this.tab = wanted;
  }

  override updated(changed: PropertyValues) {
    if (changed.has("orgId") && this.orgId) void this.load();
  }

  private async load() {
    this.loading = true;
    this.failure = null;
    this.ownerBlock = "";
    this.invites = [];
    this.invitesFailure = null;
    this.quotas = [];
    this.plan = null;
    this.quotaFailure = null;
    try {
      const { value, etag } = await getOrgWithETag(this.orgId);
      this.org = value;
      this.etag = etag;
      this.formName = value.name;
      this.formDisplayName = value.display_name;
      // The roster is viewer-and-above (`01` §4.4.1 row 1), so it always loads.
      void this.loadMembers();
      // `SC-F110`'s defaults are viewer-and-above too (row 1: view organization),
      // so they load for everyone and the FORM is what the owner check disables.
      void this.loadDefaults();
      // `SC-F112`'s posture is viewer-and-above for a reason worth restating: a
      // member refused an emailed sign-in code has to be able to find out that
      // their organization requires a passkey, and a member refused by the IP
      // allowlist has to be able to see that one exists. A posture only an owner
      // can read is one everybody else experiences as random failure.
      void this.loadSecurity();
      // Invitations are maintainer-and-above (row 3/9) and plan/quota are
      // maintainer-and-above (row 12). Below that the routes answer 403, so the
      // request is not made at all rather than made and explained away.
      if (atLeast(value.role, "maintainer")) {
        void this.loadInvites();
        void this.loadQuota();
      }
    } catch (err) {
      this.org = null;
      this.failure = parseError(err);
    } finally {
      this.loading = false;
    }
  }

  /** loadSecurity reads `SC-F112`'s posture. It never throws: the panel owns its
   * own failure state so that a 403 on this one route cannot blank the whole
   * settings tab. */
  private async loadSecurity() {
    this.securityLoading = true;
    this.securityFailure = null;
    try {
      const value = await getSecurity(this.orgId);
      this.security = value;
      this.formAllowlist = value.ip_allowlist.join(", ");
    } catch (err) {
      this.security = null;
      this.securityFailure = parseError(err);
    } finally {
      this.securityLoading = false;
    }
  }

  /**
   * toggleSecurity writes ONE switch, sending only that field.
   *
   * Only the changed field is sent because absent means unchanged (`06` §1.10):
   * two owners editing two switches in two tabs then last-write-wins on their own
   * switch rather than on each other's. The `If-Match` from the organization read
   * still guards the row, so a stale tab is told to reload rather than silently
   * overwriting.
   */
  private async toggleSecurity(
    field: "require_passkey" | "enforced_sso" | "require_test_gate" | "suppress_provenance",
    next: boolean,
  ) {
    if (!this.security) return;
    this.securitySaving = true;
    this.securityBlock = "";
    this.securityFailure = null;
    try {
      this.security = await updateSecurity(this.orgId, { [field]: next });
      toast("Security settings saved", "success");
    } catch (err) {
      const failure = parseError(err);
      if (failure.code === "forbidden") {
        this.securityBlock = failure.message;
      } else {
        this.securityFailure = failure;
      }
    } finally {
      this.securitySaving = false;
    }
  }

  /**
   * saveAllowlist writes `SC-F112`'s CIDR set from the textarea.
   *
   * The self-exclusion refusal is caught by hand and left on screen: an owner who
   * submits a range that does not contain their own attributed address has written
   * a rule that takes effect on their very next request, including the request
   * that would fix it, and there is no self-service way back. That refusal is the
   * feature — see `internal/accounts`' `errIPSelfExclusion` — so the screen has to
   * render its reason rather than a generic failure toast.
   */
  private async saveAllowlist() {
    const entries = this.formAllowlist
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter((s) => s !== "");
    this.securitySaving = true;
    this.securityBlock = "";
    this.securityFailure = null;
    try {
      this.security = await updateSecurity(this.orgId, { ip_allowlist: entries });
      this.formAllowlist = this.security.ip_allowlist.join(", ");
      toast(
        entries.length === 0
          ? "Network restriction removed"
          : `Network restriction saved (${entries.length} ${entries.length === 1 ? "range" : "ranges"})`,
        "success",
      );
    } catch (err) {
      const failure = parseError(err);
      if (failure.code === "conflict" || failure.code === "validation_failed") {
        this.securityBlock = failure.message;
      } else {
        this.securityFailure = failure;
      }
    } finally {
      this.securitySaving = false;
    }
  }

  /** openDuties loads one membership's duty configuration into the editor. */
  private async openDuties(accountId: string) {
    this.dutyTarget = accountId;
    this.dutyBlock = "";
    this.dutyFailure = null;
    this.dutyDraft = [];
    try {
      const current: MemberDuties = await getDuties(this.orgId, accountId);
      this.dutyDraft = [...current.excluded_duties];
    } catch (err) {
      this.dutyFailure = parseError(err);
    }
  }

  /**
   * saveDuties writes the absolute exclusion set.
   *
   * The `duty_coverage` refusal stays on screen with the class the change would
   * strand. That is `SC-F914`'s safety property made visible: the server refuses a
   * configuration leaving the organization with nobody able to publish or
   * administer, and a UI that toasted the refusal away would leave the owner
   * believing the boxes they ticked were saved.
   */
  private async saveDuties() {
    if (!this.dutyTarget) return;
    this.dutySaving = true;
    this.dutyBlock = "";
    this.dutyFailure = null;
    try {
      await setDuties(this.orgId, this.dutyTarget, this.dutyDraft);
      toast("Duty classes saved", "success");
      this.dutyTarget = "";
      void this.loadMembers();
    } catch (err) {
      const failure = parseError(err);
      if (failure.code === "conflict" || failure.code === "validation_failed") {
        this.dutyBlock = failure.message;
      } else {
        this.dutyFailure = failure;
      }
    } finally {
      this.dutySaving = false;
    }
  }

  private async loadMembers() {
    this.membersLoading = true;
    this.membersFailure = null;
    try {
      const page = await listMembers(this.orgId);
      this.members = page.items;
    } catch (err) {
      this.membersFailure = parseError(err);
    } finally {
      this.membersLoading = false;
    }
  }

  private async loadInvites() {
    this.invitesLoading = true;
    this.invitesFailure = null;
    try {
      const page = await listInvites(this.orgId);
      this.invites = page.items;
    } catch (err) {
      this.invitesFailure = parseError(err);
    } finally {
      this.invitesLoading = false;
    }
  }

  private async loadQuota() {
    this.quotaLoading = true;
    this.quotaFailure = null;
    try {
      // The plan is `SC-F900` (v1) and the quota list is `SC-F901` (MVP): ask
      // for both, and let a missing plan degrade to the quota list alone rather
      // than blanking the tab.
      const [quotas, plan] = await Promise.allSettled([
        getQuotas(this.orgId),
        getPlan(this.orgId),
      ]);
      if (quotas.status === "fulfilled") this.quotas = quotas.value.items;
      else this.quotaFailure = parseError(quotas.reason);
      this.plan = plan.status === "fulfilled" ? plan.value : null;
    } finally {
      this.quotaLoading = false;
    }
  }

  /* ── derived state ───────────────────────────────────────────────── */

  /** The caller's own role on this organization; `undefined` fails every gate. */
  private get actorRole(): Role | undefined {
    return this.org?.role;
  }

  private get ownerCount(): number {
    return this.members.filter((m) => m.role === "owner").length;
  }

  /**
   * ownerLock is the reason a member's role and removal controls are frozen, or
   * the empty string. It is the client's rendering of `01` §4.2 — an
   * organization keeps at least one owner, and a personal organization's owner
   * can never be removed or demoted — and it exists so the UI shows the
   * invariant instead of discovering it through a `409`.
   *
   * The server re-checks it inside one transaction, which is where the
   * concurrency case is actually decided (`SC-S108`); this is a courtesy that
   * prevents an obviously doomed request, never the enforcement.
   */
  private ownerLock(m: Member): string {
    if (m.role !== "owner") return "";
    if (this.org?.personal) {
      return "This is your personal organization. Its owner cannot be removed or demoted.";
    }
    if (this.ownerCount <= 1) {
      return "This is the organization's only owner. Promote another member to owner, or transfer ownership, first — an organization with no owner cannot be administered.";
    }
    return "";
  }

  /* ── writes ──────────────────────────────────────────────────────── */

  /**
   * refuse renders a failed write. Pass `lastOwnerFallback` from the two call
   * sites that can trip the owner invariant: a last-owner refusal is kept on
   * screen with its forward path, because `SC-S108` requires the refusal to
   * carry one and a four-second toast is not a place to put it. Everything else
   * is a toast carrying the server's own message plus the request id, so the
   * reader can quote it.
   */
  private refuse(err: unknown, lastOwnerFallback?: string) {
    const e = parseError(err);
    // `06` §3.3 answers `conflict` for the last-owner refusal. A server that
    // ships the more specific `last_owner` code `SC-S108` asks for is handled
    // here too: an unknown code keeps the server's own message.
    if (
      lastOwnerFallback &&
      (e.code === "conflict" || e.code === "last_owner")
    ) {
      this.ownerBlock = e.serverMessage || lastOwnerFallback;
      this.onTab("members");
      return;
    }
    toast(
      e.requestId ? `${e.message} (reference ${e.requestId})` : e.message,
      "error",
    );
  }

  /**
   * revertRole puts a row's picker back on the role the roster still holds.
   *
   * It has to reach for the element rather than re-render: bn-select mirrors
   * `value` on commit, and lit will not re-commit a property binding whose bound
   * value has not changed — so after an abandoned or refused change the picker
   * would keep showing a role nobody has.
   */
  private revertRole(m: Member) {
    void this.updateComplete.then(() => {
      const el = this.renderRoot.querySelector<HTMLElement & { value: string }>(
        `bn-select[data-account="${m.account}"]`,
      );
      if (el) el.value = m.role;
    });
  }

  private async changeRole(m: Member, role: Role) {
    if (role === m.role || this.busyAccount !== "") return;
    this.busyAccount = m.account;
    try {
      // Granting or withdrawing ownership is the one membership change that
      // moves authority the actor cannot get back on their own, so both
      // directions carry a step-up grant (`01` §4.4.1 rows 4 and 7; the action
      // vocabulary calls it `change_owner`, §5.4).
      let grant: string | undefined;
      if (role === "owner" || m.role === "owner") {
        grant = await this.requestGrant(
          { action: "change_owner", target: this.orgId },
          role === "owner"
            ? "Confirm the promotion to owner"
            : "Confirm the demotion",
          role === "owner"
            ? `Making ${m.email} an owner gives them every power you have, including the power to remove you.`
            : `${m.email} loses ownership of this organization, including the ability to create and delete instances.`,
        );
        if (!grant) {
          this.revertRole(m);
          return;
        }
      }
      this.ownerBlock = "";
      const updated = await setMemberRole(this.orgId, m.account, role, {
        verification: grant,
      });
      this.members = this.members.map((x) =>
        x.account === m.account ? { ...x, role: updated.role } : x,
      );
      toast(
        `${m.email} is now ${ROLE_LABELS[updated.role].label.toLowerCase()}`,
        "success",
      );
    } catch (err) {
      this.refuse(err, "An organization must always keep at least one owner.");
      this.revertRole(m);
    } finally {
      this.busyAccount = "";
    }
  }

  private async confirmRemove() {
    const m = this.removeTarget;
    if (!m || this.removing) return;
    this.removing = true;
    this.ownerBlock = "";
    try {
      await removeMember(this.orgId, m.account);
      if (m.account === this.me) {
        toast(
          `You left ${this.org?.display_name || this.org?.name || "the organization"}`,
          "success",
        );
        this.removeTarget = null;
        navigate("/orgs");
        return;
      }
      this.members = this.members.filter((x) => x.account !== m.account);
      toast(`${m.email} removed`, "success");
      this.removeTarget = null;
      this.removeConfirmText = "";
    } catch (err) {
      this.refuse(err, "An organization must always keep at least one owner.");
    } finally {
      this.removing = false;
    }
  }

  private async confirmTransfer() {
    const target = this.members.find((m) => m.account === this.nominee);
    if (!target || this.transferring) return;
    // Close the confirmation before opening the verification: two dialogs open
    // at once means two focus traps and two Escape handlers fighting over the
    // same key press.
    this.transferConfirm = false;
    const grant = await this.requestGrant(
      { action: "change_owner", target: this.orgId },
      "Confirm the ownership transfer",
      `Nominating ${target.email} as owner is the one membership change you cannot undo on your own. Confirm it is you.`,
    );
    if (!grant) return;
    this.transferring = true;
    try {
      await transferOwnership(this.orgId, target.account, {
        verification: grant,
      });
      this.nominee = "";
      toast(
        `${target.email} has been nominated as owner. The transfer completes when they accept.`,
        "success",
      );
      // The nomination does not change the roster until it is accepted, but the
      // organization record carries the caller's role — reload both.
      void this.load();
    } catch (err) {
      this.refuse(err, "The transfer was refused.");
    } finally {
      this.transferring = false;
    }
  }

  private async submitInvite() {
    if (this.inviting) return;
    const email = this.inviteEmail.trim();
    this.inviteError = "";
    this.inviteFailure = null;
    if (email === "") {
      this.inviteError = "Enter an email address.";
      return;
    }
    this.inviting = true;
    try {
      const invite = await createInvite(this.orgId, {
        email,
        role: this.inviteRole,
      });
      // Row existence is pending status, and inviting an address that already
      // has a pending row replaces it — so drop the stale copy rather than
      // showing the same address twice. Prepended because the list route sorts
      // newest first.
      this.invites = [
        invite,
        ...this.invites.filter((i) => i.email !== invite.email),
      ];
      this.inviteEmail = "";
      this.inviteRole = "member";
      toast(`Invitation sent to ${invite.email}`, "success");
    } catch (err) {
      const e = parseError(err);
      this.inviteFailure = e;
      if (e.code === "validation_failed") this.inviteError = e.message;
    } finally {
      this.inviting = false;
    }
  }

  private async doResend(inv: Invite) {
    if (this.busyInviteId !== "") return;
    this.busyInviteId = inv.id;
    this.inviteFailure = null;
    try {
      const fresh = await resendInvite(this.orgId, inv);
      this.invites = this.invites.map((i) => (i.id === inv.id ? fresh : i));
      toast(
        `A new invitation link was mailed to ${inv.email}. The previous link no longer works.`,
        "success",
      );
    } catch (err) {
      this.inviteFailure = parseError(err);
      if (this.inviteFailure.code !== "rate_limited") this.refuse(err);
    } finally {
      this.busyInviteId = "";
    }
  }

  private async doRevoke(inv: Invite) {
    if (this.busyInviteId !== "") return;
    this.busyInviteId = inv.id;
    try {
      await revokeInvite(this.orgId, inv.id);
      this.invites = this.invites.filter((i) => i.id !== inv.id);
      toast(
        `The invitation to ${inv.email} was revoked. Its link no longer works.`,
        "success",
      );
    } catch (err) {
      this.refuse(err);
    } finally {
      this.busyInviteId = "";
    }
  }

  private async saveSettings() {
    if (this.saving) return;
    this.saveError = "";
    this.saveFailure = null;
    const name = this.formName.trim();
    const displayName = this.formDisplayName.trim();
    if (name === "" || displayName === "") {
      this.saveError = "Both a name and a URL label are required.";
      return;
    }
    this.saving = true;
    try {
      await updateOrg(this.orgId, { name, display_name: displayName }, { ifMatch: this.etag });
      // Re-read rather than trust the echo: the PATCH response carries no new
      // `ETag`, and the next save needs one.
      const { value, etag } = await getOrgWithETag(this.orgId);
      this.org = value;
      this.etag = etag;
      this.formName = value.name;
      this.formDisplayName = value.display_name;
      toast("Organization updated", "success");
    } catch (err) {
      const e = parseError(err);
      this.saveFailure = e;
      if (e.code === "name_taken")
        this.saveError = "That URL label is already in use.";
      else if (e.code === "validation_failed") this.saveError = e.message;
    } finally {
      this.saving = false;
    }
  }

  /* ── the step-up ceremony (`SC-F011`) ────────────────────────────── */

  /**
   * requestGrant opens the verification dialog and resolves with a single-use
   * step-up grant, or with the empty string when the reader cancels. Callers
   * treat an empty grant as "do nothing", which keeps the cancel path out of
   * every `catch` block.
   */
  private requestGrant(
    target: StepUpTarget,
    heading: string,
    explain: string,
  ): Promise<string> {
    this.verifyTarget = target;
    this.verifyStage = "choose";
    this.verifyCode = "";
    this.verifyError = "";
    this.verifyOtpId = "";
    this.verifyPrompt = { heading, explain };
    return new Promise<string>((resolve) => {
      this.verifyResolve = resolve;
    });
  }

  private settleGrant(grant: string) {
    const resolve = this.verifyResolve;
    this.verifyResolve = null;
    this.verifyPrompt = null;
    this.verifyBusy = false;
    resolve?.(grant);
  }

  private async verifyByPasskey() {
    this.verifyBusy = true;
    this.verifyError = "";
    try {
      this.settleGrant(await verifyWithPasskey(this.verifyTarget));
    } catch (err) {
      // A dismissed browser prompt is a cancellation, not a failure — leave the
      // dialog open on the email path rather than accusing the reader of an
      // error they did not make.
      if (err instanceof DOMException) this.verifyError = "";
      else this.verifyError = parseError(err).message;
      this.verifyBusy = false;
    }
  }

  private async sendVerifyCode() {
    this.verifyBusy = true;
    this.verifyError = "";
    try {
      const { otpId } = await requestStepUpOtp();
      this.verifyOtpId = otpId;
      this.verifyStage = "code";
    } catch (err) {
      this.verifyError = parseError(err).message;
    } finally {
      this.verifyBusy = false;
    }
  }

  private async submitVerifyCode() {
    const code = this.verifyCode.trim();
    if (code === "") {
      this.verifyError = "Enter the code from the email.";
      return;
    }
    this.verifyBusy = true;
    this.verifyError = "";
    try {
      this.settleGrant(
        await confirmStepUpOtp(this.verifyOtpId, code, this.verifyTarget),
      );
    } catch (err) {
      this.verifyError = parseError(err).message;
      this.verifyBusy = false;
    }
  }

  /* ── rendering: shared pieces ────────────────────────────────────── */

  private supportRows(err: ApiError): BnKvRow[] {
    const rows: BnKvRow[] = [{ label: "Error code", value: err.code }];
    if (err.requestId) rows.push({ label: "Request id", value: err.requestId });
    return rows;
  }

  private renderFailure(
    heading: string,
    err: ApiError,
    retry: () => void,
    extraAction: TemplateResult | typeof nothing = nothing,
  ) {
    return html`<div class="failure" role="alert">
      <h2>${heading}</h2>
      <p>${err.message}</p>
      <bn-kv .rows=${this.supportRows(err)}></bn-kv>
      ${
        err.requestId
          ? nothing
          : html`<p>
              This request did not reach Sluice Cloud, so there is no reference
              to quote.
            </p>`
      }
      <div class="actions">
        <bn-button variant="secondary" @click=${retry}>Retry</bn-button
        >${extraAction}
      </div>
    </div>`;
  }

  /**
   * renderRoleGate is the `SC-S103` refusal: a surface the caller's role cannot
   * reach says which role it needs and who can grant it, rather than
   * disappearing. A hidden control teaches nobody what to ask for.
   */
  private renderRoleGate(what: string, needed: Role) {
    const have = this.actorRole;
    return html`<bn-panel eyebrow="Not available at your role">
      ${what} needs the ${ROLE_LABELS[needed].label.toLowerCase()} role or
      above.
      ${
        have
          ? html`You are a ${ROLE_LABELS[have].label.toLowerCase()} on this
            organization.`
          : nothing
      }
      Ask an owner to change your role.
    </bn-panel>`;
  }

  /* ── rendering: members ──────────────────────────────────────────── */

  /** roleOptions builds the role picker for one row, disabling the rungs the
   * caller may not grant. `owner` is owner-only (`01` §4.4.1 row 4). */
  private roleOptions(): BnSelectOption[] {
    const canGrantOwner = this.actorRole === "owner";
    return ROLES.map((r) => ({
      value: r,
      label: ROLE_LABELS[r].label,
      description: ROLE_LABELS[r].hint,
      disabled: r === "owner" && !canGrantOwner,
      disabledReason: "Only an owner can grant ownership.",
    }));
  }

  /**
   * renderDutyBadges shows `SC-F914`'s subtraction on the roster row.
   *
   * Without it a maintainer excluded from `operate` reads as an ordinary
   * maintainer and the reason their controls are missing is invisible — which is
   * the failure mode of every "effective permissions" screen that shows only the
   * role. The tone is `disabled` rather than `warning`: an exclusion is a
   * deliberate configuration, not a fault.
   */
  private renderDutyBadges(m: Member) {
    const excluded = m.excluded_duties ?? [];
    if (excluded.length === 0) return nothing;
    return html`<span class="badges">
      ${excluded.map(
        (d) =>
          html`<bn-badge
            variant="disabled"
            title=${`Excluded from the ${DUTY_LABELS[d]?.label ?? d} duty class (SC-F914)`}
            >minus ${DUTY_LABELS[d]?.label ?? d}</bn-badge
          >`,
      )}
    </span>`;
  }

  private renderRoleCell(m: Member) {
    const lock = this.ownerLock(m);
    const manageable = canManage(this.actorRole, m.role) && lock === "";
    if (!manageable) {
      return html`<span class="role-cell">
        <span
          ><bn-badge .variant=${roleVariant(m.role)}
            >${ROLE_LABELS[m.role].label}</bn-badge
          ></span
        >
        ${this.renderDutyBadges(m)}
        ${lock ? html`<span class="reason">${lock}</span>` : nothing}
      </span>`;
    }
    return html`<span class="role-cell">
      <bn-select
        aria-label=${`Role for ${m.email}`}
        data-account=${m.account}
        .options=${this.roleOptions()}
        .value=${m.role}
        ?disabled=${this.busyAccount === m.account}
        @change=${(e: CustomEvent<BnSelectChangeDetail>) =>
          void this.changeRole(m, e.detail.value as Role)}
      ></bn-select>
      ${this.renderDutyBadges(m)}
    </span>`;
  }

  private renderRemoveCell(m: Member) {
    const self = m.account === this.me;
    const lock = this.ownerLock(m);
    // Self-leave is always allowed (`01` §4.2) except where the owner lock bites
    // — the last owner and the personal organization's owner (row 8's footnote).
    const allowed = lock === "" && (self || canManage(this.actorRole, m.role));
    const reason =
      lock !== ""
        ? lock
        : allowed
          ? ""
          : "Removing this member needs a higher role.";
    return html`<span>
      <span class="cell-actions">
        <bn-button
          variant="danger"
          size="sm"
          ?disabled=${!allowed || this.busyAccount === m.account}
          @click=${() => {
            this.removeConfirmText = "";
            this.removeTarget = m;
          }}
          >${self ? "Leave" : "Remove"}</bn-button
        >
      </span>
      ${reason ? html`<span class="reason">${reason}</span>` : nothing}
    </span>`;
  }

  private memberRows(): BnRow[] {
    return this.members.map((m) => ({
      id: m.id,
      member: html`<span class="who">
        <span class="name"
          >${m.name || m.email}
          ${m.account === this.me ? html`<bn-badge variant="info">you</bn-badge>` : nothing}</span
        >
        <span class="mono">${m.email}</span>
      </span>`,
      role: this.renderRoleCell(m),
      joined: html`<span class="when">${fmtDate(m.created)}</span>`,
      actions: this.renderRemoveCell(m),
    }));
  }

  private memberColumns(): BnColumn[] {
    return [
      { key: "member", label: "Member", width: "40%" },
      { key: "role", label: "Role" },
      { key: "joined", label: "Joined" },
      { key: "actions", label: "", align: "right" },
    ];
  }

  /**
   * renderTransfer is `SC-F107`. It is on the members tab and not behind a menu
   * because it was promoted from v1 to MVP precisely so an owner has somewhere
   * obvious to hand the organization over *before* they leave: once the last
   * owner is unreachable the only remaining path is staff-mediated.
   */
  private renderTransfer() {
    if (this.actorRole !== "owner") return nothing;
    const eligible = this.members.filter(
      (m) => m.account !== this.me && m.role !== "owner",
    );
    const options: BnSelectOption[] = eligible.map((m) => ({
      value: m.account,
      label: m.name || m.email,
      description: `${m.email} · currently ${ROLE_LABELS[m.role].label.toLowerCase()}`,
    }));
    return html`<bn-card class="gap" heading="Transfer ownership">
      <p class="copy">
        Nominate another member as owner. They must accept, and you are demoted
        to maintainer rather than removed — the organization keeps at least one
        owner at every point in the sequence. Hand ownership over before you
        leave: an organization whose only owner is unreachable can only be
        recovered by Sluice Cloud staff, on a support ticket, and the only staff
        action that exists is granting owner to a named member.
      </p>
      ${
        options.length === 0
          ? html`<p class="copy">
              There is nobody to nominate yet. Invite a colleague, or promote an
              existing member, and they will appear here.
            </p>`
          : html`<div class="row">
              <bn-field class="grow" label="New owner">
                <bn-select
                  .options=${options}
                  .value=${this.nominee}
                  placeholder="Choose a member"
                  @change=${(e: CustomEvent<BnSelectChangeDetail>) => (this.nominee = e.detail.value)}
                ></bn-select>
              </bn-field>
              <bn-button
                variant="secondary"
                ?disabled=${this.nominee === ""}
                @click=${() => (this.transferConfirm = true)}
                >Transfer ownership</bn-button
              >
            </div>`
      }
    </bn-card>`;
  }

  private renderMembersTab() {
    if (this.membersLoading && this.members.length === 0) {
      return html`<bn-spinner
        block
        size="28"
        label="Loading members"
      ></bn-spinner>`;
    }
    if (this.membersFailure) {
      return html`<bn-card>
        ${this.renderFailure(
          "Could not load the member list",
          this.membersFailure,
          () => void this.loadMembers(),
        )}
      </bn-card>`;
    }
    const soleOwner = this.members.find(
      (m) => m.role === "owner" && this.ownerLock(m) !== "",
    );
    return html`
      ${
        this.ownerBlock
          ? html`<bn-panel
              class="gap"
              eyebrow="Refused: the organization would have no owner"
            >
              ${this.ownerBlock} The way forward is to give the organization a
              second owner first:
              ${
              this.actorRole === "owner"
                ? html`promote another member to owner in the table below, or
                  use ownership transfer underneath it.`
                : html`ask an owner to promote another member, since only an
                  owner can grant ownership.`
            }
            </bn-panel>`
          : nothing
      }
      ${
        soleOwner
          ? html`<bn-panel eyebrow="Owner protection">
              ${soleOwner.name || soleOwner.email}: ${this.ownerLock(soleOwner)}
              The controls that would do it are disabled rather than offered and
              then refused.
            </bn-panel>`
          : nothing
      }
      <bn-card class=${this.ownerBlock || soleOwner ? "gap" : ""} flush>
        <bn-table
          caption="Members of this organization"
          .columns=${this.memberColumns()}
          .rows=${this.memberRows()}
          ?loading=${this.membersLoading}
          empty="This organization has no members."
        ></bn-table>
      </bn-card>
      ${this.renderTransfer()}
    `;
  }

  /* ── rendering: invitations ──────────────────────────────────────── */

  private inviteRoleOptions(): BnSelectOption[] {
    const canInviteOwner = this.actorRole === "owner";
    return ROLES.map((r) => ({
      value: r,
      label: ROLE_LABELS[r].label,
      description: ROLE_LABELS[r].hint,
      disabled: r === "owner" && !canInviteOwner,
      disabledReason: "Only an owner can invite another owner.",
    }));
  }

  private inviteRows(): BnRow[] {
    return this.invites.map((inv) => {
      const expiry = expiryLabel(inv.expires);
      const busy = this.busyInviteId === inv.id;
      // Owner invitations are owner-only to create, so they are owner-only to
      // manage — mirror the rule rather than offering a button the route
      // refuses.
      const manageable = atLeast(
        this.actorRole,
        inv.role === "owner" ? "owner" : "maintainer",
      );
      return {
        id: inv.id,
        email: html`<span class="mono">${inv.email}</span>`,
        role: html`<bn-badge .variant=${roleVariant(inv.role)}
          >${ROLE_LABELS[inv.role].label}</bn-badge
        >`,
        invited: html`<span class="when">${inv.invited_by_label || "—"}</span>`,
        expires: expiry.expired
          ? html`<bn-badge variant="warning">expired</bn-badge>`
          : html`<span class="when">${expiry.text}</span>`,
        actions: html`<span class="cell-actions">
          <bn-button
            size="sm"
            ?disabled=${!manageable || busy}
            @click=${() => void this.doResend(inv)}
            >Resend</bn-button
          >
          <bn-button
            variant="danger"
            size="sm"
            ?disabled=${!manageable || busy}
            @click=${() => void this.doRevoke(inv)}
            >Revoke</bn-button
          >
        </span>`,
      };
    });
  }

  /**
   * renderInviteFailure surfaces the two refusals an invite create can carry
   * that are not field errors. The send budget is the interesting one: `SC-F105`
   * brakes invitations with a **per-actor stock budget**, not a rate-limit
   * label, because an exact label would have to contain `{orgId}` and labels are
   * matched against the literal request path — so the honest sentence says what
   * the budget actually bounds, and that a resend spends it too.
   */
  private renderInviteFailure() {
    const e = this.inviteFailure;
    if (!e) return nothing;
    if (e.code === "rate_limited") {
      return html`<bn-panel eyebrow="Invitation send budget reached">
        You have sent as many invitations as one account may send in this
        window. Wait, then try again. Re-sending an existing invitation spends
        the same budget as a new one. The budget is per account rather than per
        organization: it exists so a compromised account cannot use Sluice
        Cloud's mail reputation as a weapon.
      </bn-panel>`;
    }
    if (e.code === "quota_exceeded") {
      const detail = e.details as unknown as QuotaDetail | undefined;
      return html`<bn-panel eyebrow="Member quota reached">
        ${
          detail && typeof detail.limit === "number"
            ? html`Your plan allows ${detail.limit} members and this
              organization has ${detail.current}.`
            : html`This invitation would exceed your organization's member
              quota.`
        }
        Plan changes are not self-service — contact support to raise the limit.
      </bn-panel>`;
    }
    if (e.code === "validation_failed") return nothing;
    return html`<bn-panel eyebrow="The invitation was not sent">
      ${e.message}${e.requestId ? html` (reference ${e.requestId})` : nothing}
    </bn-panel>`;
  }

  private renderInvitationsTab() {
    if (!atLeast(this.actorRole, "maintainer")) {
      return this.renderRoleGate("Managing invitations", "maintainer");
    }
    if (this.invitesLoading && this.invites.length === 0) {
      return html`<bn-spinner
        block
        size="28"
        label="Loading invitations"
      ></bn-spinner>`;
    }
    return html`
      <bn-card heading="Invite a colleague">
        <p class="copy">
          They receive an email with a link. The link works whether or not they
          already have a Sluice Cloud account, and it can be used once.
          Accepting binds the membership to the account they sign in with, even
          if that account's address differs from the one invited.
        </p>
        ${this.renderInviteFailure()}
        <div class="row">
          <bn-field
            class="grow"
            label="Email address"
            required
            error=${this.inviteError}
            hint="No account needed yet — the link is also a sign-up."
          >
            <bn-input
              type="email"
              inputmode="email"
              autocomplete="off"
              placeholder="colleague@example.com"
              .value=${this.inviteEmail}
              ?invalid=${this.inviteError !== ""}
              @input=${(e: CustomEvent<{ value: string }>) => {
                this.inviteEmail = e.detail.value;
                this.inviteError = "";
              }}
              @keydown=${(e: KeyboardEvent) => {
                if (e.key === "Enter") void this.submitInvite();
              }}
            ></bn-input>
          </bn-field>
          <bn-field class="role-field" label="Role">
            <bn-select
              .options=${this.inviteRoleOptions()}
              .value=${this.inviteRole}
              @change=${(e: CustomEvent<BnSelectChangeDetail>) =>
                (this.inviteRole = e.detail.value as Role)}
            ></bn-select>
          </bn-field>
          <bn-button
            variant="primary"
            ?loading=${this.inviting}
            @click=${() => void this.submitInvite()}
            >Send invitation</bn-button
          >
        </div>
      </bn-card>

      <bn-card class="gap" flush heading="Pending invitations">
        ${
          this.invitesFailure
            ? html`<div style="padding: 0 var(--bino-space-lg)">
                ${this.renderFailure(
                "Could not load invitations",
                this.invitesFailure,
                () => void this.loadInvites(),
              )}
              </div>`
            : html`<bn-table
                caption="Pending invitations"
                .columns=${
                [
                  { key: "email", label: "Invited" },
                  { key: "role", label: "Role" },
                  { key: "invited", label: "Invited by" },
                  { key: "expires", label: "Expires" },
                  { key: "actions", label: "", align: "right", width: "1px" },
                ] as BnColumn[]
              }
                .rows=${this.inviteRows()}
                ?loading=${this.invitesLoading}
              >
                <bn-empty-state
                  slot="empty"
                  icon="✉"
                  heading="No pending invitations"
                  message="An invitation appears here until it is accepted, revoked or expires."
                ></bn-empty-state>
              </bn-table>`
        }
      </bn-card>
    `;
  }

  /* ── rendering: settings ─────────────────────────────────────────── */

  /* ── instance defaults (`SC-F110`) ─────────────────────────────────── */

  private async loadDefaults() {
    this.defaultsLoading = true;
    this.defaultsFailure = null;
    try {
      const value = await getOrgInstanceDefaults(this.orgId);
      this.defaults = value;
      this.formDefaultSize = value.size;
    } catch (err) {
      this.defaults = null;
      this.defaultsFailure = parseError(err);
    } finally {
      this.defaultsLoading = false;
    }
  }

  /**
   * saveDefaults writes the size default. The pool default is deliberately NOT
   * editable here — see [renderDefaultsPanel].
   *
   * The empty string is sent as an explicit clear rather than omitted, because
   * "no organization default" is a real choice an organization makes when it stops
   * standardizing on a size, and omitting the field would mean "leave it alone".
   */
  private async saveDefaults() {
    if (this.defaultsSaving) return;
    this.defaultsSaving = true;
    try {
      const value = await setOrgInstanceDefaults(this.orgId, {
        size: this.formDefaultSize,
      });
      this.defaults = value;
      this.formDefaultSize = value.size;
      toast("Instance defaults saved", "success");
    } catch (err) {
      const e = parseError(err);
      this.defaultsFailure = e;
      toast(
        e.requestId ? `${e.message} (reference ${e.requestId})` : e.message,
        "error",
      );
    } finally {
      this.defaultsSaving = false;
    }
  }

  /**
   * renderDefaultsPanel is `SC-F110`'s settings surface.
   *
   * **The `applies` sentence is rendered verbatim and is the point of the panel.**
   * The server composes it so no client has to, and the half that matters is that
   * changing a default moves nothing and restarts nothing: a customer who believes
   * this resizes their fleet will never touch it again, and one who believes it
   * does not when it does has a much worse afternoon.
   *
   * **The node-pool default is shown read-only, and that is honest rather than
   * lazy.** The API accepts a pool id, but there is no route in this build that
   * LISTS the pools a customer may choose from — `SC-F310`'s pool inventory is a
   * staff surface (`SC-F904`, unimplemented) — so a picker here would either be an
   * empty dropdown or a free-text field for an opaque record id. The panel shows
   * what is set, names why it is not editable, and leaves the API able to do more
   * than the form: that is the one direction `concept.md` principle 6 allows.
   *
   * The four states are the page's own: loading is the spinner, empty is the
   * "no default set" copy, error carries the code and the reference, populated is
   * the form.
   */
  private renderDefaultsPanel() {
    const isOwner = this.actorRole === "owner";
    if (this.defaultsLoading && !this.defaults) {
      return html`<bn-card class="gap" heading="Defaults for new instances">
        <bn-spinner block size="24" label="Loading defaults"></bn-spinner>
      </bn-card>`;
    }
    if (this.defaultsFailure && !this.defaults) {
      return html`<bn-card class="gap" heading="Defaults for new instances">
        ${this.renderFailure(
          "Could not load the defaults",
          this.defaultsFailure,
          () => void this.loadDefaults(),
        )}
      </bn-card>`;
    }
    const d = this.defaults;
    if (!d) return nothing;

    const sizeOptions: BnSelectOption[] = [
      { value: "", label: "No default — choose per instance" },
      ...d.sizes.map((s) => ({ value: s, label: s })),
    ];
    return html`<bn-card class="gap" heading="Defaults for new instances">
      <p class="copy">${d.applies}</p>
      ${
        isOwner
          ? nothing
          : html`<p class="copy">
              Editing these is owner-only. They are shown read-only at your
              role.
            </p>`
      }
      <bn-field
        label="Size class"
        hint="Prefilled in the create-instance wizard. A create request that names its own size wins."
      >
        <bn-select
          .options=${sizeOptions}
          .value=${this.formDefaultSize}
          ?disabled=${!isOwner || this.defaultsSaving}
          @change=${(e: CustomEvent<BnSelectChangeDetail>) => {
            this.formDefaultSize = e.detail.value;
          }}
        ></bn-select>
      </bn-field>
      <bn-field
        label="Node pool"
        hint="Set through the API. This build has no route that lists the node pools an organization may choose from, so a picker here would be an empty dropdown — the value is shown rather than offered."
      >
        <bn-input
          mono
          disabled
          .value=${d.node_pool_name || d.node_pool}
          placeholder="No default — the platform decides placement"
        ></bn-input>
      </bn-field>
      ${
        isOwner
          ? html`<bn-button
              variant="primary"
              ?loading=${this.defaultsSaving}
              ?disabled=${this.formDefaultSize === d.size}
              @click=${() => void this.saveDefaults()}
              >Save defaults</bn-button
            >`
          : nothing
      }
    </bn-card>`;
  }

  private renderSettingsTab() {
    const isOwner = this.actorRole === "owner";
    const failure = this.saveFailure;
    return html`
      <bn-card heading="Organization details">
        ${
          isOwner
            ? nothing
            : html`<p class="copy">
                Renaming an organization is owner-only. These values are shown
                read-only at your role.
              </p>`
        }
        ${
          failure && failure.code === "precondition_failed"
            ? html`<bn-panel eyebrow="Someone else changed this">
                ${failure.message}
                <bn-button
                  size="sm"
                  variant="secondary"
                  @click=${() => void this.load()}
                  >Reload</bn-button
                >
              </bn-panel>`
            : nothing
        }
        ${
          failure &&
          failure.code !== "precondition_failed" &&
          failure.code !== "name_taken" &&
          failure.code !== "validation_failed"
            ? html`<div class="failure" role="alert">
                <p>${failure.message}</p>
                <bn-kv .rows=${this.supportRows(failure)}></bn-kv>
              </div>`
            : nothing
        }

        <bn-field
          label="Name"
          required
          hint="How this organization is named throughout Sluice Cloud."
        >
          <bn-input
            .value=${this.formDisplayName}
            ?disabled=${!isOwner}
            @input=${(e: CustomEvent<{ value: string }>) => {
              this.formDisplayName = e.detail.value;
              this.saveError = "";
            }}
          ></bn-input>
        </bn-field>
        <bn-field
          label="URL label"
          required
          error=${this.saveError}
          hint="Lowercase letters, digits and hyphens. Renaming does not change any instance hostname — a hostname is allocated from the instance name, so a connection string in a BI tool keeps working."
        >
          <bn-input
            mono
            .value=${this.formName}
            ?disabled=${!isOwner}
            ?invalid=${this.saveError !== ""}
            @input=${(e: CustomEvent<{ value: string }>) => {
              this.formName = e.detail.value;
              this.saveError = "";
            }}
          ></bn-input>
        </bn-field>
        ${
          isOwner
            ? html`<bn-button
                variant="primary"
                ?loading=${this.saving}
                @click=${() => void this.saveSettings()}
                >Save changes</bn-button
              >`
            : nothing
        }
      </bn-card>

      ${this.renderDefaultsPanel()}

      ${this.renderSecurityPanel(isOwner)} ${this.renderDutiesPanel(isOwner)}
    `;
  }

  /**
   * renderSecurityPanel is `SC-F112`. Four states, in the order `SC-S907`
   * requires them: loading, error (with the `apierr` code and the request id),
   * empty, populated.
   *
   * The empty state is real rather than theoretical: a member below owner reads
   * the posture fine, but a deployment whose migration has not run answers a 500
   * and the panel must say which route failed rather than rendering an
   * all-switches-off form that looks like a configured organization.
   */
  private renderSecurityPanel(isOwner: boolean) {
    if (this.securityLoading && !this.security) {
      return html`<bn-card class="gap" heading="Security">
        <bn-spinner block size="28" label="Loading security settings"></bn-spinner>
      </bn-card>`;
    }
    if (this.securityFailure) {
      return html`<bn-card class="gap" heading="Security">
        ${this.renderFailure(
          "Could not load security settings",
          this.securityFailure,
          () => void this.loadSecurity(),
        )}
      </bn-card>`;
    }
    if (!this.security) {
      return html`<bn-card class="gap" heading="Security">
        <bn-empty-state
          heading="No security settings"
          body="This deployment did not return a security posture for this organization. Nothing is enforced until it does."
        ></bn-empty-state>
      </bn-card>`;
    }

    const sec = this.security;
    return html`
      <bn-card class="gap" heading="Security">
        <p class="copy">
          ${isOwner
            ? "Every switch here is applied by the running build. The note under each one is the boundary of what it applies to — read it before you rely on it."
            : "Every switch here is applied by the running build. Changing them is owner-only; they are shown so you can see what applies to your sign-in and your requests."}
        </p>
        ${this.securityBlock
          ? html`<bn-panel eyebrow="Not saved" role="alert">
              ${this.securityBlock}
            </bn-panel>`
          : nothing}
        ${SECURITY_SETTINGS.map(
          (s) => html`<div class="setting">
            <bn-checkbox
              label=${s.label}
              hint=${s.hint}
              ?checked=${sec[s.field]}
              ?disabled=${!isOwner || this.securitySaving}
              @change=${(e: CustomEvent<{ checked: boolean }>) =>
                void this.toggleSecurity(s.field, e.detail.checked)}
            ></bn-checkbox>
          </div>`,
        )}
        ${SECURITY_SETTINGS.filter((s) => s.limit !== "").map((s) => {
          const value = sec.limits[s.limit as keyof SecurityLimits];
          return html`<p class="limit">
            <strong>${s.label}</strong> — ${LIMIT_COPY[value] ?? value}
          </p>`;
        })}

        <div class="setting">
          <bn-field
            class="grow"
            label="Restrict access to these networks"
            hint="Comma-separated IP addresses or CIDR ranges. Leave empty for no restriction. A bare address is stored as a single-host range."
          >
            <bn-input
              mono
              placeholder="203.0.113.0/24, 198.51.100.7"
              .value=${this.formAllowlist}
              ?disabled=${!isOwner || this.securitySaving}
              @input=${(e: CustomEvent<{ value: string }>) =>
                (this.formAllowlist = e.detail.value)}
            ></bn-input>
          </bn-field>
        </div>
        <p class="limit">
          ${LIMIT_COPY[sec.limits.ip_allowlist_scope] ?? sec.limits.ip_allowlist_scope}
        </p>
        ${isOwner
          ? html`<bn-button
              variant="secondary"
              ?loading=${this.securitySaving}
              @click=${() => void this.saveAllowlist()}
              >Save network restriction</bn-button
            >`
          : nothing}

        <p class="limit">
          <strong>Session lifetime</strong> —
          ${LIMIT_COPY[sec.limits.session_lifetime] ?? sec.limits.session_lifetime}
        </p>
        <p class="limit">
          <strong>Audit retention</strong> —
          ${sec.audit_retention_days > 0
            ? `${sec.audit_retention_days} days.`
            : "the platform default."}
          Changing it is a billing action, not a membership one, so it is not on
          this panel.
        </p>
      </bn-card>
    `;
  }

  /**
   * renderDutiesPanel is `SC-F914`: an owner subtracts duty classes from one
   * membership.
   *
   * It is a panel on the Settings tab rather than a control inside the roster
   * table, and that is a deliberate placement: subtracting a class is a rarer and
   * more consequential act than changing a role, the refusal it can produce needs
   * room to be read, and putting it inside a table row would make the whole roster
   * a security-configuration surface.
   */
  private renderDutiesPanel(isOwner: boolean) {
    if (!isOwner) return nothing;
    const target = this.members.find((m) => m.account === this.dutyTarget) ?? null;
    return html`
      <bn-card class="gap" heading="Separation of duties">
        <p class="copy">
          A membership carries its role MINUS zero or more duty classes. Excluding
          a class refuses every action in it, whatever the role — so a maintainer
          minus <em>Operate</em> can still author and publish policy but cannot
          restart an instance.
        </p>
        <p class="copy">
          A configuration that would leave this organization with nobody holding a
          class is refused rather than saved: an organization where nobody can
          publish, or nobody can administer, has no way back that does not involve
          us.
        </p>
        <bn-field label="Member" hint="Pick the membership to narrow.">
          <bn-select
            .options=${this.dutyMemberOptions()}
            .value=${this.dutyTarget}
            @change=${(e: CustomEvent<BnSelectChangeDetail>) =>
              void this.openDuties(e.detail.value)}
          ></bn-select>
        </bn-field>
        ${this.dutyFailure
          ? this.renderFailure(
              "Could not load duty classes",
              this.dutyFailure,
              () => void this.openDuties(this.dutyTarget),
            )
          : nothing}
        ${this.dutyBlock
          ? html`<bn-panel eyebrow="Not saved" role="alert">
              ${this.dutyBlock}
            </bn-panel>`
          : nothing}
        ${target
          ? html`
              ${DUTIES.map(
                (d) => html`<div class="setting">
                  <bn-checkbox
                    label=${`Exclude ${DUTY_LABELS[d].label}`}
                    hint=${DUTY_LABELS[d].hint}
                    ?checked=${this.dutyDraft.includes(d)}
                    ?disabled=${this.dutySaving}
                    @change=${(e: CustomEvent<{ checked: boolean }>) => {
                      this.dutyDraft = e.detail.checked
                        ? [...this.dutyDraft, d]
                        : this.dutyDraft.filter((x) => x !== d);
                      this.dutyBlock = "";
                    }}
                  ></bn-checkbox>
                </div>`,
              )}
              <bn-button
                variant="primary"
                ?loading=${this.dutySaving}
                @click=${() => void this.saveDuties()}
                >Save duty classes for ${target.email || target.account}</bn-button
              >
            `
          : nothing}
      </bn-card>
    `;
  }

  /** dutyMemberOptions lists the roster for the duty picker, with each member's
   * current exclusions in the label so an owner can see the shape of the
   * organization before they change it. */
  private dutyMemberOptions(): BnSelectOption[] {
    const options: BnSelectOption[] = [{ value: "", label: "Select a member…" }];
    for (const m of this.members) {
      const excluded = m.excluded_duties ?? [];
      options.push({
        value: m.account,
        label:
          `${m.email || m.account} · ${ROLE_LABELS[m.role]?.label ?? m.role}` +
          (excluded.length > 0 ? ` · minus ${excluded.join(", ")}` : ""),
      });
    }
    return options;
  }

  /* ── rendering: quota ────────────────────────────────────────────── */

  private renderMeter(q: Quota) {
    const label = quotaLabel(q.quota);
    // A non-positive limit is "this plan sets no ceiling here". A bar with no
    // ceiling is a bar with no meaning, so the row states the consumption and
    // leaves the meter out.
    const unlimited = !Number.isFinite(q.limit) || q.limit <= 0;
    const pct = unlimited
      ? 0
      : Math.min(100, Math.round((q.current / q.limit) * 100));
    return html`<div class="quota-row">
      <div class="quota-head">
        <span class="quota-name">${label}</span>
        <span class="quota-value"
          >${unlimited ? `${q.current} · no limit set` : `${q.current} of ${q.limit}`}</span
        >
      </div>
      ${
        unlimited
          ? nothing
          : html`<div
              class="meter"
              role="meter"
              aria-label=${`${label} consumption`}
              aria-valuemin="0"
              aria-valuemax=${q.limit}
              aria-valuenow=${q.current}
              aria-valuetext=${`${q.current} of ${q.limit} used`}
            >
              <div class="meter-fill" style=${`width:${pct}%`}></div>
            </div>`
      }
    </div>`;
  }

  private renderQuotaTab() {
    if (!atLeast(this.actorRole, "maintainer")) {
      return this.renderRoleGate("Plan, quota and usage", "maintainer");
    }
    if (this.quotaLoading && this.quotas.length === 0) {
      return html`<bn-spinner
        block
        size="28"
        label="Loading quota"
      ></bn-spinner>`;
    }
    if (this.quotaFailure) {
      return html`<bn-card>
        ${this.renderFailure("Could not load quota", this.quotaFailure, () => void this.loadQuota())}
      </bn-card>`;
    }
    const plan = this.plan;
    // `SC-F900` is v1: a build that serves quotas without a plan record is a
    // valid build, and a missing `features` array is not a reason to blank the
    // tab that also carries the MVP quota figures.
    const features = plan?.features ?? [];
    return html`
      <bn-card heading="Plan">
        ${
          plan
            ? html`
                <bn-kv
                  .rows=${
                  [
                    { label: "Plan", value: plan.name },
                    {
                      label: "Audit retention",
                      value: `${plan.audit_retention_days} days`,
                    },
                  ] as BnKvRow[]
                }
                ></bn-kv>
                ${
                features.length > 0
                  ? html`<div class="features gap">
                      ${features.map((f) => html`<bn-badge variant="neutral">${f}</bn-badge>`)}
                    </div>`
                  : nothing
              }
                <p class="copy gap">
                  Plan changes are not self-service in this build. Contact
                  support to change a limit.
                </p>
              `
            : html`<p class="copy">
                This build serves quotas without a named plan. The ceilings
                below are the ones in force for this organization.
              </p>`
        }
      </bn-card>

      <bn-card class="gap" heading="Consumption">
        ${
          this.quotas.length === 0
            ? html`<bn-empty-state
                icon="∅"
                heading="No quotas reported"
                message="This organization has no entitlement ceilings recorded."
              ></bn-empty-state>`
            : this.quotas.map((q) => this.renderMeter(q))
        }
      </bn-card>
    `;
  }

  /* ── rendering: dialogs ──────────────────────────────────────────── */

  private renderRemoveDialog() {
    const m = this.removeTarget;
    const self = m?.account === this.me;
    const orgName = this.org?.display_name || this.org?.name || "this organization";
    // Removing another account's **owner** row is `01` §4.4.1 row 7, gated at
    // MVP on "owner-only plus typed confirmation". The role gate is already on
    // the button; this is the typing.
    const typed = m !== null && m.role === "owner" && !self;
    const close = () => {
      this.removeTarget = null;
      this.removeConfirmText = "";
    };
    return html`<bn-modal
      ?open=${m !== null}
      tone="destructive"
      heading=${self ? "Leave this organization?" : "Remove this member?"}
      .width=${520}
      @close=${close}
    >
      ${
        m
          ? html`<p class="copy">
              ${
              self
                ? html`You will lose access to <strong>${orgName}</strong> and
                    everything it owns.`
                : html`Remove <strong>${m.email}</strong> from
                    <strong>${orgName}</strong>?`
            }
            </p>`
          : nothing
      }
      ${
        typed && m
          ? html`<bn-field
              label="Type the member's email address to confirm"
              required
              hint="Removing an owner is confirmed by hand, because it is the one removal that can change who controls this organization."
            >
              <bn-input
                mono
                .value=${this.removeConfirmText}
                placeholder=${m.email}
                @input=${(e: CustomEvent<{ value: string }>) =>
                (this.removeConfirmText = e.detail.value)}
              ></bn-input>
            </bn-field>`
          : nothing
      }
      <p slot="consequence" style="margin:0">
        Access ends immediately, and so does the reach of any fine-grained API
        token scoped to this organization: a token's authority is intersected
        with its owner's live role on every request, so nothing has to be
        revoked separately.
      </p>
      <bn-button slot="footer" variant="ghost" @click=${close}
        >Cancel</bn-button
      >
      <bn-button
        slot="footer"
        variant="danger"
        ?disabled=${typed && this.removeConfirmText.trim() !== m?.email}
        ?loading=${this.removing}
        @click=${() => void this.confirmRemove()}
        >${self ? "Leave organization" : "Remove member"}</bn-button
      >
    </bn-modal>`;
  }

  private renderTransferDialog() {
    const target = this.members.find((m) => m.account === this.nominee);
    return html`<bn-modal
      ?open=${this.transferConfirm}
      tone="destructive"
      heading="Transfer ownership?"
      .width=${560}
      @close=${() => (this.transferConfirm = false)}
    >
      ${
        target
          ? html`<p style="margin:0">
              <strong>${target.email}</strong> will be nominated as owner of
              <strong>${this.org?.display_name || this.org?.name}</strong>. Nothing
              changes until they accept.
            </p>`
          : nothing
      }
      <p slot="consequence" style="margin:0">
        When they accept, you are demoted to maintainer. You will no longer be
        able to create or delete instances, manage members, or take ownership
        back on your own — only the new owner can transfer it again.
      </p>
      <bn-button
        slot="footer"
        variant="ghost"
        @click=${() => (this.transferConfirm = false)}
        >Cancel</bn-button
      >
      <bn-button
        slot="footer"
        variant="danger"
        ?loading=${this.transferring}
        @click=${() => void this.confirmTransfer()}
        >Nominate as owner</bn-button
      >
    </bn-modal>`;
  }

  private renderVerifyDialog() {
    const prompt = this.verifyPrompt;
    return html`<bn-modal
      ?open=${prompt !== null}
      heading=${prompt?.heading ?? "Confirm it is you"}
      .width=${480}
      @close=${() => this.settleGrant("")}
    >
      <p class="copy">${prompt?.explain ?? ""}</p>
      ${
        this.verifyError
          ? html`<p class="copy error-text" role="alert">
              ${this.verifyError}
            </p>`
          : nothing
      }
      ${
        this.verifyStage === "choose"
          ? html`<div class="row">
              <bn-button
                variant="primary"
                ?loading=${this.verifyBusy}
                @click=${() => void this.verifyByPasskey()}
                >Verify with a passkey</bn-button
              >
              <bn-button
                ?loading=${this.verifyBusy}
                @click=${() => void this.sendVerifyCode()}
                >Email me a code</bn-button
              >
            </div>`
          : html`<bn-field
              label="Verification code"
              required
              hint="Sent to your account's email address. It is valid once, for a few minutes."
            >
              <bn-input
                mono
                inputmode="numeric"
                autocomplete="one-time-code"
                .value=${this.verifyCode}
                @input=${(e: CustomEvent<{ value: string }>) => (this.verifyCode = e.detail.value)}
                @keydown=${(e: KeyboardEvent) => {
                if (e.key === "Enter") void this.submitVerifyCode();
              }}
              ></bn-input>
            </bn-field>`
      }
      <bn-button
        slot="footer"
        variant="ghost"
        @click=${() => this.settleGrant("")}
        >Cancel</bn-button
      >
      ${
        this.verifyStage === "code"
          ? html`<bn-button
              slot="footer"
              variant="primary"
              ?loading=${this.verifyBusy}
              @click=${() => void this.submitVerifyCode()}
              >Confirm</bn-button
            >`
          : nothing
      }
    </bn-modal>`;
  }

  /* ── render ──────────────────────────────────────────────────────── */

  private onTab(id: string) {
    this.tab = id;
    // The tab is a view of one route, not a route of its own, so it rides in the
    // query string and replaces rather than pushes: a tab click should not cost
    // a back-button press to undo.
    //
    // The pathname is rewritten to the canonical `/orgs/:orgId` rather than kept
    // as-is, so that switching tabs from `/orgs/:id/members` cannot leave the
    // self-contradicting `/orgs/:id/members?tab=settings` in the URL bar. Only
    // the address changes — the router is not re-entered, and this element stays
    // mounted, which is the point of a tab.
    history.replaceState(
      {},
      "",
      `${hrefFor("orgs", this.orgId)}?tab=${encodeURIComponent(id)}`,
    );
  }

  override render() {
    // `this.orgId` empty means the element was rendered outside its route; fall
    // through to the failure branch rather than spinning forever.
    if (this.loading && this.orgId) {
      return html`<bn-spinner
        block
        size="28"
        label="Loading organization"
      ></bn-spinner>`;
    }
    if (this.failure || !this.org) {
      const err =
        this.failure ??
        new ApiError({
          code: "not_found",
          message: "That organization does not exist.",
        });
      return html`
        <a class="back" href="/orgs">← Organizations</a>
        <bn-card>
          ${this.renderFailure(
            "Could not load this organization",
            err,
            () => void this.load(),
            html`<bn-button variant="ghost" @click=${() => navigate("/orgs")}
              >Back to organizations</bn-button
            >`,
          )}
        </bn-card>
      `;
    }

    const org = this.org;
    return html`
      <a class="back" href="/orgs">← Organizations</a>
      <div class="head">
        <h1>${org.display_name || org.name}</h1>
        <div class="meta">
          <span class="slug">${org.name}</span>
          ${
            org.personal
              ? html`<bn-badge
                  variant="neutral"
                  title="Created for you when you signed up."
                  >personal organization</bn-badge
                >`
              : nothing
          }
          ${
            org.role
              ? html`<bn-badge .variant=${roleVariant(org.role)}
                  >you are
                  ${ROLE_LABELS[org.role].label.toLowerCase()}</bn-badge
                >`
              : nothing
          }
        </div>
      </div>

      <bn-tabs
        label="Organization sections"
        .tabs=${TABS}
        .active=${this.tab}
        @change=${(e: CustomEvent<BnTabsChangeDetail>) => this.onTab(e.detail.id)}
      >
        <div slot="members">${this.renderMembersTab()}</div>
        <div slot="invitations">${this.renderInvitationsTab()}</div>
        <div slot="settings">${this.renderSettingsTab()}</div>
        <div slot="quota">${this.renderQuotaTab()}</div>
      </bn-tabs>

      ${this.renderRemoveDialog()} ${this.renderTransferDialog()}
      ${this.renderVerifyDialog()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-org": PageOrg;
  }
}
